import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { post, get, patch, del } from 'aws-amplify/api';
import { useAuth } from '../../contexts/AuthContext';

const ReservationContext = createContext(null);

export const useReservations = () => {
  const context = useContext(ReservationContext);
  if (!context) {
    throw new Error('useReservations must be used within a ReservationProvider');
  }
  return context;
};

export const ReservationProvider = ({ children }) => {
  const { getAuthToken } = useAuth();

  // Loading and error states
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  // Dashboard state
  const [currentTime, setCurrentTime] = useState(new Date());
  const [dashboardData, setDashboardData] = useState({
    servicePeriod: 'Dinner',
    tablesByStatus: { available: 0, seated: 0, reserved: 0, bussing: 0 },
    coversSeated: 0,
    totalCapacity: 70,
    arrivingSoon: [],
    alerts: [],
  });

  // Waitlist state
  const [waitlist, setWaitlist] = useState([]);

  // Search/Arrival state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);

  // Reservations state
  const [reservations, setReservations] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [stats, setStats] = useState(null);

  // Tables state
  const [tables, setTables] = useState([]);
  const [tableStats, setTableStats] = useState(null);

  // Settings state
  const [settings, setSettings] = useState(null);
  const [settingsFormData, setSettingsFormData] = useState({
    interval_minutes: 15,
    operating_hours_start: '11:00',
    operating_hours_end: '22:00',
    default_turn_time: 90,
    max_party_size: 12,
    walk_in_reserve_percent: 20,
    total_capacity: 70,
    is_active: true,
  });

  // Zones state
  const [zones, setZones] = useState([]);
  const [unassignedTables, setUnassignedTables] = useState([]);
  const [zoneAvailability, setZoneAvailability] = useState(null);
  const [zoneAvailabilityDate, setZoneAvailabilityDate] = useState(new Date().toISOString().split('T')[0]);

  // Floor Map Designer state
  const [floorMapMode, setFloorMapMode] = useState('view');
  const [selectedFloor, setSelectedFloor] = useState(1);
  const [floorLayout, setFloorLayout] = useState({ zones: {}, tables: {} });
  const [draggedItem, setDraggedItem] = useState(null);
  const [selectedMapItem, setSelectedMapItem] = useState(null);

  // Calendar state
  const [currentDate, setCurrentDate] = useState(new Date());
  const [currentView, setCurrentView] = useState('day');

  // Update current time every minute
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 60000);
    return () => clearInterval(timer);
  }, []);

  // Clear messages after 5 seconds
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  // ===== API Functions =====

  const fetchReservations = async () => {
    try {
      setLoading(true);
      setError(null);

      const token = await getAuthToken();
      let path = `/v1/admin/reservations?date=${selectedDate}`;
      if (statusFilter !== 'all') {
        path = `/v1/admin/reservations?status=${statusFilter}`;
      }

      const restOperation = get({
        apiName: 'ChulasAdminAPI',
        path,
        options: { headers: { Authorization: token } },
      });

      const response = await restOperation.response;
      const data = await response.body.json();

      setReservations(data.reservations || []);
      setStats(data.stats || null);
    } catch (err) {
      console.error('Error fetching reservations:', err);
      setError('Failed to fetch reservations');
    } finally {
      setLoading(false);
    }
  };

  const getServicePeriod = () => {
    const hour = currentTime.getHours();
    if (hour < 11) return 'Closed';
    if (hour < 15) return 'Lunch';
    if (hour < 17) return 'Transition';
    if (hour < 21) return 'Dinner';
    if (hour < 22) return 'Late';
    return 'Closed';
  };

  const fetchDashboardData = async () => {
    try {
      const token = await getAuthToken();
      const today = new Date().toISOString().split('T')[0];

      const restOperation = get({
        apiName: 'ChulasAdminAPI',
        path: `/v1/admin/reservations?date=${today}`,
        options: { headers: { Authorization: token } },
      });

      const response = await restOperation.response;
      const data = await response.body.json();
      const todayReservations = data.reservations || [];

      const now = new Date();

      // Find reservations arriving in next 30 minutes
      const arrivingSoon = todayReservations.filter(r => {
        if (r.status !== 'confirmed') return false;
        const resDate = new Date(`${today}T${r.time}`);
        const diffMins = (resDate - now) / 60000;
        return diffMins >= -15 && diffMins <= 30;
      }).sort((a, b) => a.time.localeCompare(b.time));

      // Calculate covers seated
      const seatedReservations = todayReservations.filter(r => r.status === 'seated');
      const coversSeated = seatedReservations.reduce((sum, r) => sum + (r.party_size || 0), 0);

      // Find late reservations
      const lateReservations = todayReservations.filter(r => {
        if (r.status !== 'confirmed') return false;
        const resDate = new Date(`${today}T${r.time}`);
        const diffMins = (now - resDate) / 60000;
        return diffMins > 15;
      });

      // Build alerts
      const alerts = [];
      lateReservations.forEach(r => {
        const resDate = new Date(`${today}T${r.time}`);
        const minsLate = Math.round((now - resDate) / 60000);
        alerts.push({
          type: 'late',
          message: `${r.customer_name} is ${minsLate} mins late`,
          reservation: r,
        });
      });

      arrivingSoon.filter(r => r.special_requests?.toLowerCase().includes('vip')).forEach(r => {
        alerts.push({
          type: 'vip',
          message: `VIP ${r.customer_name} arriving at ${r.time}`,
          reservation: r,
        });
      });

      setDashboardData({
        servicePeriod: getServicePeriod(),
        tablesByStatus: { available: 0, seated: 0, reserved: 0, bussing: 0 },
        coversSeated,
        totalCapacity: data.stats?.total_covers || 70,
        arrivingSoon,
        alerts,
        todayReservations,
        stats: data.stats,
      });

      const waitlistItems = todayReservations.filter(r => r.status === 'waitlist');
      setWaitlist(waitlistItems);

    } catch (err) {
      console.error('Error fetching dashboard data:', err);
    }
  };

  const searchReservations = (query) => {
    if (!query.trim()) {
      setSearchResults([]);
      return;
    }
    const q = query.toLowerCase();
    const results = (dashboardData.todayReservations || []).filter(r => {
      return (
        r.customer_name?.toLowerCase().includes(q) ||
        r.customer_phone?.includes(q) ||
        r.time?.includes(q) ||
        r.confirmation_code?.toLowerCase().includes(q)
      );
    });
    setSearchResults(results);
  };

  const createReservation = async (formData) => {
    try {
      setLoading(true);
      const token = await getAuthToken();

      const restOperation = post({
        apiName: 'ChulasAdminAPI',
        path: '/v1/admin/reservations',
        options: {
          headers: { Authorization: token, 'Content-Type': 'application/json' },
          body: formData,
        },
      });

      await restOperation.response;
      setSuccessMessage('Reservation created successfully');
      fetchReservations();
      return true;
    } catch (err) {
      console.error('Error creating reservation:', err);
      setError('Failed to create reservation');
      return false;
    } finally {
      setLoading(false);
    }
  };

  const updateReservationStatus = async (reservation, newStatus, additionalData = {}) => {
    try {
      setLoading(true);
      const token = await getAuthToken();

      const restOperation = patch({
        apiName: 'ChulasAdminAPI',
        path: `/v1/admin/reservations/${reservation.reservation_id}/status`,
        options: {
          headers: { Authorization: token, 'Content-Type': 'application/json' },
          body: { status: newStatus, ...additionalData },
        },
      });

      await restOperation.response;
      setSuccessMessage(`Reservation marked as ${newStatus}`);
      fetchReservations();
      fetchDashboardData();
      return true;
    } catch (err) {
      console.error('Error updating status:', err);
      setError('Failed to update reservation status');
      return false;
    } finally {
      setLoading(false);
    }
  };

  const cancelReservation = async (reservation) => {
    try {
      setLoading(true);
      const token = await getAuthToken();

      const restOperation = del({
        apiName: 'ChulasAdminAPI',
        path: `/v1/admin/reservations/${reservation.reservation_id}?date=${reservation.reservation_date}`,
        options: { headers: { Authorization: token } },
      });

      await restOperation.response;
      setSuccessMessage('Reservation cancelled');
      fetchReservations();
      return true;
    } catch (err) {
      console.error('Error cancelling reservation:', err);
      setError('Failed to cancel reservation');
      return false;
    } finally {
      setLoading(false);
    }
  };

  const createWalkIn = async (walkInData) => {
    try {
      setLoading(true);
      const token = await getAuthToken();

      const restOperation = post({
        apiName: 'ChulasAdminAPI',
        path: '/v1/admin/reservations/walk-in',
        options: {
          headers: { Authorization: token, 'Content-Type': 'application/json' },
          body: walkInData,
        },
      });

      await restOperation.response;
      setSuccessMessage('Walk-in checked in successfully');
      fetchReservations();
      return true;
    } catch (err) {
      console.error('Error creating walk-in:', err);
      setError('Failed to create walk-in');
      return false;
    } finally {
      setLoading(false);
    }
  };

  const addToWaitlist = async (waitlistData) => {
    try {
      setLoading(true);
      const token = await getAuthToken();
      const today = new Date().toISOString().split('T')[0];
      const now = new Date().toTimeString().slice(0, 5);

      const restOperation = post({
        apiName: 'ChulasAdminAPI',
        path: '/v1/admin/reservations',
        options: {
          headers: { Authorization: token, 'Content-Type': 'application/json' },
          body: {
            date: today,
            time: now,
            party_size: waitlistData.party_size,
            customer_name: waitlistData.customer_name || 'Walk-in',
            customer_phone: waitlistData.customer_phone,
            special_requests: waitlistData.notes,
            source: 'walk-in',
            status: 'waitlist',
          },
        },
      });

      await restOperation.response;
      setSuccessMessage('Added to waitlist');
      fetchDashboardData();
      return true;
    } catch (err) {
      console.error('Error adding to waitlist:', err);
      setError('Failed to add to waitlist');
      return false;
    } finally {
      setLoading(false);
    }
  };

  // ===== Tables API Functions =====

  const fetchTables = async () => {
    try {
      setLoading(true);
      const token = await getAuthToken();

      const restOperation = get({
        apiName: 'ChulasAdminAPI',
        path: '/v1/admin/tables',
        options: { headers: { Authorization: token } },
      });

      const response = await restOperation.response;
      const data = await response.body.json();

      setTables(data.tables || []);
      setTableStats(data.stats || null);
    } catch (err) {
      console.error('Error fetching tables:', err);
      setError('Failed to fetch tables');
    } finally {
      setLoading(false);
    }
  };

  const createTable = async (tableData) => {
    try {
      setLoading(true);
      const token = await getAuthToken();

      const restOperation = post({
        apiName: 'ChulasAdminAPI',
        path: '/v1/admin/tables',
        options: {
          headers: { Authorization: token, 'Content-Type': 'application/json' },
          body: tableData,
        },
      });

      await restOperation.response;
      setSuccessMessage('Table created successfully');
      fetchTables();
      return true;
    } catch (err) {
      console.error('Error creating table:', err);
      setError('Failed to create table');
      return false;
    } finally {
      setLoading(false);
    }
  };

  const updateTable = async (tableId, tableData) => {
    try {
      setLoading(true);
      const token = await getAuthToken();

      const restOperation = patch({
        apiName: 'ChulasAdminAPI',
        path: `/v1/admin/tables/${tableId}`,
        options: {
          headers: { Authorization: token, 'Content-Type': 'application/json' },
          body: tableData,
        },
      });

      await restOperation.response;
      setSuccessMessage('Table updated successfully');
      fetchTables();
      return true;
    } catch (err) {
      console.error('Error updating table:', err);
      setError('Failed to update table');
      return false;
    } finally {
      setLoading(false);
    }
  };

  const deleteTable = async (table) => {
    try {
      setLoading(true);
      const token = await getAuthToken();

      const restOperation = del({
        apiName: 'ChulasAdminAPI',
        path: `/v1/admin/tables/${table.table_id}`,
        options: { headers: { Authorization: token } },
      });

      await restOperation.response;
      setSuccessMessage('Table removed successfully');
      fetchTables();
      return true;
    } catch (err) {
      console.error('Error deleting table:', err);
      setError('Failed to delete table');
      return false;
    } finally {
      setLoading(false);
    }
  };

  // ===== Settings API Functions =====

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const token = await getAuthToken();

      const restOperation = get({
        apiName: 'ChulasAdminAPI',
        path: '/v1/admin/reservations/settings',
        options: { headers: { Authorization: token } },
      });

      const response = await restOperation.response;
      const data = await response.body.json();

      setSettings(data);
      if (data.global_settings) {
        setSettingsFormData(prev => ({ ...prev, ...data.global_settings }));
      }
    } catch (err) {
      console.error('Error fetching settings:', err);
      setError('Failed to fetch settings');
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    try {
      setLoading(true);
      const token = await getAuthToken();

      const restOperation = patch({
        apiName: 'ChulasAdminAPI',
        path: '/v1/admin/reservations/settings',
        options: {
          headers: { Authorization: token, 'Content-Type': 'application/json' },
          body: {
            setting_type: 'global',
            setting_id: 'default',
            ...settingsFormData,
          },
        },
      });

      await restOperation.response;
      setSuccessMessage('Settings saved successfully');
      fetchSettings();
      return true;
    } catch (err) {
      console.error('Error saving settings:', err);
      setError('Failed to save settings');
      return false;
    } finally {
      setLoading(false);
    }
  };

  // ===== Zones API Functions =====

  const fetchZones = async () => {
    try {
      setLoading(true);
      const token = await getAuthToken();

      const restOperation = get({
        apiName: 'ChulasAdminAPI',
        path: '/v1/admin/zones',
        options: { headers: { Authorization: token } },
      });

      const response = await restOperation.response;
      const data = await response.body.json();

      setZones(data.zones || []);
      setUnassignedTables(data.unassigned_tables || []);
    } catch (err) {
      console.error('Error fetching zones:', err);
      setError('Failed to fetch zones');
    } finally {
      setLoading(false);
    }
  };

  const fetchZoneAvailability = async (date) => {
    try {
      setLoading(true);
      const token = await getAuthToken();

      const restOperation = get({
        apiName: 'ChulasAdminAPI',
        path: `/v1/admin/zones/availability?date=${date}`,
        options: { headers: { Authorization: token } },
      });

      const response = await restOperation.response;
      const data = await response.body.json();

      setZoneAvailability(data);
    } catch (err) {
      console.error('Error fetching zone availability:', err);
      setError('Failed to fetch zone availability');
    } finally {
      setLoading(false);
    }
  };

  const createZone = async (zoneData) => {
    try {
      setLoading(true);
      const token = await getAuthToken();

      const restOperation = post({
        apiName: 'ChulasAdminAPI',
        path: '/v1/admin/zones',
        options: {
          headers: { Authorization: token, 'Content-Type': 'application/json' },
          body: zoneData,
        },
      });

      await restOperation.response;
      setSuccessMessage('Zone created successfully');
      fetchZones();
      return true;
    } catch (err) {
      console.error('Error creating zone:', err);
      setError('Failed to create zone');
      return false;
    } finally {
      setLoading(false);
    }
  };

  const updateZone = async (zoneId, zoneData) => {
    try {
      setLoading(true);
      const token = await getAuthToken();

      const restOperation = patch({
        apiName: 'ChulasAdminAPI',
        path: `/v1/admin/zones/${zoneId}`,
        options: {
          headers: { Authorization: token, 'Content-Type': 'application/json' },
          body: zoneData,
        },
      });

      await restOperation.response;
      setSuccessMessage('Zone updated successfully');
      fetchZones();
      return true;
    } catch (err) {
      console.error('Error updating zone:', err);
      setError('Failed to update zone');
      return false;
    } finally {
      setLoading(false);
    }
  };

  const deleteZone = async (zone) => {
    try {
      setLoading(true);
      const token = await getAuthToken();

      const restOperation = del({
        apiName: 'ChulasAdminAPI',
        path: `/v1/admin/zones/${zone.zone_id}`,
        options: { headers: { Authorization: token } },
      });

      await restOperation.response;
      setSuccessMessage('Zone deleted successfully');
      fetchZones();
      return true;
    } catch (err) {
      console.error('Error deleting zone:', err);
      setError('Failed to delete zone');
      return false;
    } finally {
      setLoading(false);
    }
  };

  const assignTableToZone = async (zoneId, tableId) => {
    try {
      setLoading(true);
      const token = await getAuthToken();

      const restOperation = post({
        apiName: 'ChulasAdminAPI',
        path: `/v1/admin/zones/${zoneId}/tables`,
        options: {
          headers: { Authorization: token, 'Content-Type': 'application/json' },
          body: { table_id: tableId },
        },
      });

      await restOperation.response;
      setSuccessMessage(`Table ${tableId} assigned to zone`);
      fetchZones();
      return true;
    } catch (err) {
      console.error('Error assigning table:', err);
      setError('Failed to assign table to zone');
      return false;
    } finally {
      setLoading(false);
    }
  };

  const removeTableFromZone = async (zoneId, tableId) => {
    try {
      setLoading(true);
      const token = await getAuthToken();

      const restOperation = del({
        apiName: 'ChulasAdminAPI',
        path: `/v1/admin/zones/${zoneId}/tables/${tableId}`,
        options: { headers: { Authorization: token } },
      });

      await restOperation.response;
      setSuccessMessage(`Table ${tableId} removed from zone`);
      fetchZones();
      return true;
    } catch (err) {
      console.error('Error removing table:', err);
      setError('Failed to remove table from zone');
      return false;
    } finally {
      setLoading(false);
    }
  };

  // ===== Floor Map Functions =====

  const loadFloorLayout = useCallback(() => {
    const layout = { zones: {}, tables: {} };

    zones.forEach((zone, index) => {
      layout.zones[zone.zone_id] = {
        ...zone,
        x: zone.map_x ?? 50 + (index % 3) * 250,
        y: zone.map_y ?? 50 + Math.floor(index / 3) * 200,
        width: zone.map_width ?? 220,
        height: zone.map_height ?? 180,
        color: zone.map_color ?? getZoneColor(zone.zone_id),
      };
    });

    tables.forEach((table, index) => {
      const zoneLayout = layout.zones[table.zone_id];
      const tablesInZone = tables.filter(t => t.zone_id === table.zone_id);
      const tableIndex = tablesInZone.findIndex(t => t.table_id === table.table_id);

      let defaultX = 100 + (index % 8) * 70;
      let defaultY = 400 + Math.floor(index / 8) * 70;

      if (zoneLayout) {
        const cols = Math.ceil(Math.sqrt(tablesInZone.length));
        const col = tableIndex % cols;
        const row = Math.floor(tableIndex / cols);
        defaultX = zoneLayout.x + 20 + col * 55;
        defaultY = zoneLayout.y + 40 + row * 55;
      }

      const shape = table.map_shape ?? getTableShape(table.table_type);
      const defaultSize = getDefaultTableSize(shape);

      layout.tables[table.table_id] = {
        ...table,
        x: table.map_x ?? defaultX,
        y: table.map_y ?? defaultY,
        shape,
        rotation: table.map_rotation ?? 0,
        width: table.map_width ?? defaultSize.width,
        height: table.map_height ?? defaultSize.height,
      };
    });

    setFloorLayout(layout);
  }, [zones, tables]);

  const getZoneColor = (zoneId) => {
    const colors = {
      'jalisco': '#e0e7ff',
      'zacatecas': '#fce7f3',
      'main-dining': '#dcfce7',
      'patio': '#fef3c7',
      'bar': '#dbeafe',
    };
    return colors[zoneId] || '#f3f4f6';
  };

  const getTableShape = (tableType) => {
    switch (tableType) {
      case '2-top': return 'round';
      case '4-top': return 'square';
      case '6-top': return 'rectangle';
      case 'bar': return 'bar';
      case 'large': return 'rectangle';
      default: return 'square';
    }
  };

  const getDefaultTableSize = (shape) => {
    switch (shape) {
      case 'round': return { width: 50, height: 50 };
      case 'square': return { width: 50, height: 50 };
      case 'rectangle': return { width: 80, height: 50 };
      case 'bar': return { width: 120, height: 40 };
      default: return { width: 50, height: 50 };
    }
  };

  const saveFloorLayout = async () => {
    try {
      setLoading(true);
      const token = await getAuthToken();

      for (const [zoneId, zoneData] of Object.entries(floorLayout.zones)) {
        const restOperation = patch({
          apiName: 'ChulasAdminAPI',
          path: `/v1/admin/zones/${zoneId}`,
          options: {
            headers: { Authorization: token, 'Content-Type': 'application/json' },
            body: {
              map_x: zoneData.x,
              map_y: zoneData.y,
              map_width: zoneData.width,
              map_height: zoneData.height,
              map_color: zoneData.color,
            },
          },
        });
        await restOperation.response;
      }

      for (const [tableId, tableData] of Object.entries(floorLayout.tables)) {
        const restOperation = patch({
          apiName: 'ChulasAdminAPI',
          path: `/v1/admin/tables/${tableId}`,
          options: {
            headers: { Authorization: token, 'Content-Type': 'application/json' },
            body: {
              map_x: tableData.x,
              map_y: tableData.y,
              map_width: tableData.width,
              map_height: tableData.height,
              map_shape: tableData.shape,
              map_rotation: tableData.rotation,
            },
          },
        });
        await restOperation.response;
      }

      setSuccessMessage('Floor layout saved successfully');
      setFloorMapMode('view');
      return true;
    } catch (err) {
      console.error('Error saving floor layout:', err);
      setError('Failed to save floor layout');
      return false;
    } finally {
      setLoading(false);
    }
  };

  // ===== Helper Functions =====

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed': return '#3b82f6';
      case 'seated': return '#22c55e';
      case 'completed': return '#9ca3af';
      case 'cancelled': return '#ef4444';
      case 'no_show': return '#f97316';
      default: return '#6b7280';
    }
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'confirmed': return 'status-badge status-confirmed';
      case 'seated': return 'status-badge status-seated';
      case 'completed': return 'status-badge status-completed';
      case 'cancelled': return 'status-badge status-cancelled';
      case 'no_show': return 'status-badge status-noshow';
      default: return 'status-badge';
    }
  };

  const value = {
    // State
    loading,
    error,
    successMessage,
    currentTime,
    dashboardData,
    waitlist,
    searchQuery,
    setSearchQuery,
    searchResults,
    reservations,
    selectedDate,
    setSelectedDate,
    statusFilter,
    setStatusFilter,
    stats,
    tables,
    tableStats,
    settings,
    settingsFormData,
    setSettingsFormData,
    zones,
    unassignedTables,
    zoneAvailability,
    zoneAvailabilityDate,
    setZoneAvailabilityDate,
    floorMapMode,
    setFloorMapMode,
    selectedFloor,
    setSelectedFloor,
    floorLayout,
    setFloorLayout,
    draggedItem,
    setDraggedItem,
    selectedMapItem,
    setSelectedMapItem,
    currentDate,
    setCurrentDate,
    currentView,
    setCurrentView,
    setError,
    setSuccessMessage,

    // API Functions
    fetchReservations,
    fetchDashboardData,
    searchReservations,
    createReservation,
    updateReservationStatus,
    cancelReservation,
    createWalkIn,
    addToWaitlist,
    fetchTables,
    createTable,
    updateTable,
    deleteTable,
    fetchSettings,
    saveSettings,
    fetchZones,
    fetchZoneAvailability,
    createZone,
    updateZone,
    deleteZone,
    assignTableToZone,
    removeTableFromZone,
    loadFloorLayout,
    saveFloorLayout,
    getZoneColor,
    getTableShape,

    // Helpers
    getServicePeriod,
    getStatusColor,
    getStatusBadgeClass,
  };

  return (
    <ReservationContext.Provider value={value}>
      {children}
    </ReservationContext.Provider>
  );
};

export default ReservationContext;
